import { useMemo } from 'react';

/**
 * /auth/choose — workspace picker.
 *
 * Entry: platform.khal.ai/v1/auth/callback redirects here with
 *   #token=<jwt>&hint=<kernel-url>
 *
 * The JWT's `kernels` claim lists every workspace the platform considers
 * reachable. This screen decodes the claim client-side (no verification —
 * the kernel re-verifies the signature at /api/auth/accept) and renders a
 * tile per workspace. Click a tile → redirect to /auth/accept#kernel=X&token=Y,
 * which is the existing server-verified handoff.
 *
 * The picker always renders when reached (no fast-path skip even for
 * single-kernel claims) — choosing a workspace IS the primary sign-in
 * gesture and users have explicitly asked to confirm which instance
 * they're entering before landing on a desktop.
 *
 * No silent fallbacks: a malformed token, zero kernels, or decode error
 * renders a loud error, never a stale desktop.
 */

/**
 * `kernels` may arrive as either:
 *  - the legacy `string[]` of kernel URLs (pre-enterprise-customer-onboarding),
 *  - or the enriched `EnrichedKernel[]` shape Group 6 will populate once the
 *    new Customer registry is live: each entry carries `customerId`,
 *    `customerName`, and `env` so the picker can group by Customer and label
 *    rows by environment (production / staging / sandbox / per-region).
 *
 * The component renders both shapes — there is no breaking change for users
 * holding tokens issued before Group 6 lands.
 */
interface EnrichedKernel {
	url: string;
	customerId?: string;
	customerName?: string;
	env?: string;
}

type KernelClaim = string | EnrichedKernel;

interface JwtClaims {
	sub?: string;
	email?: string;
	kernels?: KernelClaim[];
	iss?: string;
	exp?: number;
}

function normalizeKernel(claim: KernelClaim): EnrichedKernel {
	return typeof claim === 'string' ? { url: claim } : claim;
}

function groupKernels(kernels: EnrichedKernel[]): Array<{ key: string; label: string; entries: EnrichedKernel[] }> {
	const groups = new Map<string, { label: string; entries: EnrichedKernel[] }>();
	for (const k of kernels) {
		const key = k.customerId ?? k.url;
		const label = k.customerName ?? kernelLabel(k.url);
		const existing = groups.get(key);
		if (existing) {
			existing.entries.push(k);
		} else {
			groups.set(key, { label, entries: [k] });
		}
	}
	return Array.from(groups.entries()).map(([key, value]) => ({ key, ...value }));
}

function parseFragment(hash: string): { token: string | null; hint: string | null } {
	const h = hash.startsWith('#') ? hash.slice(1) : hash;
	const params = new URLSearchParams(h);
	return { token: params.get('token'), hint: params.get('hint') };
}

function decodeJwtPayload(token: string): JwtClaims | null {
	try {
		const parts = token.split('.');
		if (parts.length !== 3) return null;
		const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
		const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
		return JSON.parse(atob(padded)) as JwtClaims;
	} catch {
		return null;
	}
}

function kernelLabel(kernelUrl: string): string {
	try {
		const { hostname } = new URL(kernelUrl);
		return hostname.split('.')[0] || hostname;
	} catch {
		return kernelUrl;
	}
}

function kernelHost(kernelUrl: string): string {
	try {
		return new URL(kernelUrl).hostname;
	} catch {
		return kernelUrl;
	}
}

function handoffToAccept(kernelUrl: string, token: string): void {
	window.location.replace(
		`/auth/accept#kernel=${encodeURIComponent(kernelUrl)}&token=${encodeURIComponent(token)}`,
	);
}

export function AuthChooseScreen() {
	const { token } = useMemo(() => parseFragment(window.location.hash), []);
	const claims = useMemo(() => (token ? decodeJwtPayload(token) : null), [token]);
	const rawKernels = claims?.kernels ?? [];
	const kernels = useMemo(() => rawKernels.map(normalizeKernel), [rawKernels]);
	const groups = useMemo(() => groupKernels(kernels), [kernels]);

	// Loud failure for malformed / empty-claim tokens — never fall through.
	if (!token) {
		return (
			<ChooseShell>
				<ErrorBlock
					title="Sign-in link is missing a token"
					detail="Return to the sign-in page and try again."
				/>
			</ChooseShell>
		);
	}
	if (!claims || kernels.length === 0) {
		return (
			<ChooseShell>
				<ErrorBlock
					title="No workspaces available"
					detail={
						claims?.email
							? `${claims.email} is not assigned to any Khal workspace. Contact your administrator.`
							: 'Your sign-in link did not carry any workspace assignments.'
					}
				/>
			</ChooseShell>
		);
	}

	const showGrouping = groups.some((g) => g.entries.length > 1 || g.entries.some((e) => e.env));

	return (
		<ChooseShell>
			<div style={{ width: '100%', maxWidth: 520 }}>
				<header style={{ marginBottom: '2rem', textAlign: 'center' }}>
					<h1 style={{ fontSize: '1.5rem', margin: '0 0 0.5rem 0', color: '#f4f4f5' }}>
						Choose a workspace
					</h1>
					{claims.email && (
						<p style={{ margin: 0, color: '#71717a', fontSize: '0.9rem' }}>Signed in as {claims.email}</p>
					)}
				</header>

				<div style={{ display: 'grid', gap: '1.25rem' }}>
					{groups.map((group) => (
						<section key={group.key} style={{ display: 'grid', gap: '0.5rem' }}>
							{showGrouping && (
								<h2
									style={{
										fontSize: '0.75rem',
										fontWeight: 600,
										color: '#a1a1aa',
										textTransform: 'uppercase',
										letterSpacing: '0.04em',
										margin: '0 0 0.25rem 0',
									}}
								>
									{group.label}
								</h2>
							)}
							<ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.5rem' }}>
								{group.entries.map((kernel) => (
									<li key={kernel.url}>
										<button
											type="button"
											onClick={() => handoffToAccept(kernel.url, token)}
											style={{
												width: '100%',
												display: 'flex',
												alignItems: 'center',
												gap: '1rem',
												padding: '1rem 1.25rem',
												background: '#18181b',
												border: '1px solid #27272a',
												borderRadius: '10px',
												cursor: 'pointer',
												textAlign: 'left',
												color: '#e4e4e7',
												fontFamily: 'inherit',
												fontSize: '0.95rem',
												transition: 'background 0.15s, border-color 0.15s',
											}}
											onMouseEnter={(e) => {
												e.currentTarget.style.background = '#27272a';
												e.currentTarget.style.borderColor = '#3f3f46';
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.background = '#18181b';
												e.currentTarget.style.borderColor = '#27272a';
											}}
										>
											<div
												style={{
													width: 44,
													height: 44,
													flexShrink: 0,
													borderRadius: 8,
													background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													fontSize: '1.1rem',
													fontWeight: 600,
													color: 'white',
												}}
											>
												{(kernel.customerName ?? kernelLabel(kernel.url)).slice(0, 2).toUpperCase()}
											</div>
											<div style={{ flex: 1, minWidth: 0 }}>
												<div style={{ fontWeight: 600, marginBottom: 2 }}>
													{kernel.env ?? kernelLabel(kernel.url)}
												</div>
												<div
													style={{
														color: '#71717a',
														fontSize: '0.8rem',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
												>
													{kernelHost(kernel.url)}
												</div>
											</div>
											<div style={{ color: '#52525b', fontSize: '1.2rem' }}>›</div>
										</button>
									</li>
								))}
							</ul>
						</section>
					))}
				</div>
			</div>
		</ChooseShell>
	);
}

function ChooseShell({ children }: { children: React.ReactNode }) {
	return (
		<div
			style={{
				position: 'fixed',
				inset: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: '#0a0a0a',
				color: '#e4e4e7',
				fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
				padding: '2rem',
			}}
		>
			{children}
		</div>
	);
}

function ErrorBlock({ title, detail }: { title: string; detail: string }) {
	return (
		<div style={{ textAlign: 'center', maxWidth: 440 }}>
			<h1 style={{ fontSize: '1.3rem', margin: '0 0 0.75rem 0' }}>{title}</h1>
			<p style={{ color: '#71717a', margin: 0, lineHeight: 1.5 }}>{detail}</p>
			<p style={{ marginTop: '1.5rem' }}>
				<a href="/" style={{ color: '#3b82f6' }}>
					Back to sign-in
				</a>
			</p>
		</div>
	);
}
